import React from 'react';
import { useTranslation } from 'react-i18next';
import { lireEnBase64 } from '../../components/Utils/lireEnBase64';

/**
 * Ce que partagent les écrans des dossiers disciplinaires (contrat C39,
 * Yros/Kavero#15) : les énumérations du contrat, la pastille d'état et le
 * message d'un refus.
 */

// Énumérations normatives de C39 : des clés d'un contrat fermé, pas des
// libellés d'Odoo, donc traduites (comme `departs.etats`).
export const ETATS_DOSSIER = ['ouvert', 'clos', 'annule'];
// 171a ne filtre que la convocation ; 173 ajoute les autres types d'étape.
export const TYPES_ETAPE = ['convocation'];

// Refus de C39 dont le portail a le libellé traduit (`discipline.refus.<motif>`).
const MOTIFS_CONNUS = [
  'non_autorise', 'salarie_introuvable', 'valeurs_invalides', 'dossier_introuvable',
  'dossier_clos', 'motif_requis', 'etape_introuvable', 'deja_emise', 'pas_emise', 'fichier_invalide',
];

const COULEURS_ETAT = {
  ouvert: 'bg-blue-50 text-blue-700',
  clos: 'bg-green-50 text-green-700',
  annule: 'bg-red-50 text-red-700',
};

export function EtatDossier({ etat }) {
  const { t } = useTranslation();
  return (
    <span data-testid="dossier-etat"
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${COULEURS_ETAT[etat] || 'bg-gray-100 text-gray-700'}`}>
      {t(`discipline.etats.${etat}`, { defaultValue: etat })}
    </span>
  );
}

// `reponse` est l'enveloppe rendue par `enveloppeDe` : celle d'un refus 409, ou
// `{}` pour toute panne. Décision humaine du bolt 171a : le libellé traduit d'un
// motif connu passe AVANT le `message` d'Odoo (le portail est bilingue, ce
// message est en français) ; le message n'est que le repli d'un motif inconnu.
// `contexte` (facultatif) : un libellé propre à un écran (`discipline.refus.<contexte>.<motif>`)
// passe avant le libellé général du motif.
export function messageRefus(reponse, t, contexte) {
  const { motif, message } = reponse || {};
  if (MOTIFS_CONNUS.includes(motif)) {
    const general = t(`discipline.refus.${motif}`);
    return contexte ? t(`discipline.refus.${contexte}.${motif}`, { defaultValue: general }) : general;
  }
  if (motif && typeof message === 'string' && message) return message;
  return t('discipline.refus.generique');
}

// Même borne qu'Odoo (`_copie_signee`) : 10 Mo.
export const TAILLE_MAX_COPIE_SIGNEE = 10 * 1024 * 1024;
// Même règle qu'Odoo, pas plus stricte : taille avant lecture, puis en-tête `%PDF-` (ni MIME ni extension).
// `null` = refus local, le volet le traite comme un refus `fichier_invalide`.
export async function lireCopieSignee(fichier) {
  if (fichier.size > TAILLE_MAX_COPIE_SIGNEE) return null;
  const base64 = await lireEnBase64(fichier);
  try {
    return atob(base64.slice(0, 8)).startsWith('%PDF-') ? base64 : null;
  } catch {
    return null;
  }
}
// `null` (non calculé) n'est jamais `false` : le portail n'affirme pas un délai manqué.
export const cleDelai = (v) => (v === true ? 'respecte' : v === false ? 'nonRespecte' : 'nonCalcule');
